import { useState, useCallback, useRef, useEffect } from 'react';

// Animation timing constants
const ENTER_DELAY = 10; // time for browser to paint the initial (hidden) frame
const ENTER_DURATION = 300; // total entering → open transition
const EXIT_DURATION = 300;
const SCALE_FACTOR = 0.9;
const MODAL_SCALE = 1 / SCALE_FACTOR; // ~1.1111

/**
 * Transition modals matching an id + fromState to a new animationState.
 */
const _transitionState = (stack, id, fromState, toState) => {
  const fromStates = Array.isArray(fromState) ? fromState : [fromState];
  return stack.map((m) =>
    m._id === id && fromStates.includes(m.animationState)
      ? { ...m, animationState: toState }
      : m
  );
};

/**
 * Set animationState by index.
 */
const _transitionStateByIndex = (stack, idx, toState) =>
  stack.map((m, i) => (i === idx ? { ...m, animationState: toState } : m));

/**
 * Finalize a modal close: remove from stack and invoke callbacks.
 */
const _finalizeModalClose = (modalId, closeResolversRef, setModalStack) => {
  const resolverInfo = closeResolversRef.current.get(modalId);

  setModalStack((prev) => prev.filter((m) => m._id !== modalId));

  /* istanbul ignore else -- @preserve defensive: closeModal always stores resolver before exiting */
  if (resolverInfo) {
    closeResolversRef.current.delete(modalId);
    if (resolverInfo.onCloseCallback) {
      resolverInfo.onCloseCallback(resolverInfo.callbackData);
    }
    resolverInfo.resolve();
  }
};

/**
 * Hook that provides the complete modal management logic.
 *
 * Design decisions:
 * - All animation is CSS-driven via class toggling (no direct DOM manipulation)
 * - State is fully managed in React (no imperative style.xxx = yyy)
 * - Infinite stacking supported via an array of ModalEntry objects
 * - Each modal has an animationState that drives CSS classes
 * - Wrapper scaling is computed as a derived value, not set imperatively
 *
 * @returns {Object} Modal context value
 */
export const useModalManager = () => {
  const [modalStack, setModalStack] = useState([]);
  const closeResolversRef = useRef(new Map());
  const modalIdCounterRef = useRef(0);

  /**
   * Opens a new modal on top of the stack.
   *
   * @param {import('./ModalContext').ModalDefinition} modalDef - The modal definition
   * @param {*} [data] - Arbitrary data passed to the modal component as props
   * @param {Function|null} [onCloseCallback] - Called when the modal closes, receives callbackData
   */
  const openModal = useCallback((modalDef, data, onCloseCallback) => {
    const id = ++modalIdCounterRef.current;

    const entry = {
      ...modalDef,
      _id: id,
      data: data || null,
      onCloseCallback: onCloseCallback || null,
      animationState: 'mounting', // Frame 1: invisible, at start position
    };

    setModalStack((prev) => [...prev, entry]);

    // Frame 2: after browser paints the invisible state, trigger entering (visible + slide)
    // This ensures CSS transitions animate both opacity AND transform simultaneously
    setTimeout(() => {
      setModalStack((prev) => _transitionState(prev, id, 'mounting', 'entering'));
    }, ENTER_DELAY);

    // Frame 3: entering → open (stable at final position)
    setTimeout(() => {
      setModalStack((prev) => _transitionState(prev, id, ['entering', 'mounting'], 'open'));
    }, ENTER_DELAY + ENTER_DURATION);
  }, []);

  /**
   * Closes a modal (defaults to the topmost).
   *
   * @param {number} [modalIndex] - Index of the modal to close (defaults to last)
   * @param {*} [callbackData] - Data passed to the onCloseCallback
   * @returns {Promise<void>} Resolves when the close animation finishes
   */
  const closeModal = useCallback((modalIndex, callbackData) => {
    return new Promise((resolve) => {
      setModalStack((prev) => {
        if (prev.length === 0) {
          resolve();
          return prev;
        }

        const idx = modalIndex === undefined ? prev.length - 1 : modalIndex;
        const modal = prev[idx];

        if (!modal || modal.animationState === 'exiting') {
          resolve();
          return prev;
        }

        // Store resolver + callback info
        closeResolversRef.current.set(modal._id, {
          resolve,
          callbackData,
          onCloseCallback: modal.onCloseCallback,
        });

        // Set animationState to 'exiting' — CSS handles the visual transition
        return _transitionStateByIndex(prev, idx, 'exiting');
      });
    });
  }, []);

  // Effect: when a modal finishes its exit animation, remove it from the stack
  useEffect(() => {
    const exitingModals = modalStack.filter((m) => m.animationState === 'exiting');

    const timers = exitingModals.map((modal) =>
      setTimeout(() => {
        _finalizeModalClose(modal._id, closeResolversRef, setModalStack);
      }, EXIT_DURATION)
    );

    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [modalStack]);

  /**
   * Compute wrapper transform for the stacking/scaling effect.
   * Each open modal shrinks the wrapper by SCALE_FACTOR.
   */
  const activeCount = modalStack.filter((m) => m.animationState !== 'exiting').length;
  const wrapperScale = activeCount > 0 ? Math.pow(SCALE_FACTOR, activeCount) : 1;

  return {
    modalStack,
    openModal,
    closeModal,
    wrapperScale,
    activeCount,
    MODAL_SCALE,
  };
};
